import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchApproximateLocation, fetchCoordinatesForLocation, getDistanceMiles, normalizeZip } from './locationUtils';
import { getHospitalAvatar, formatDollars, PinIcon, DocIcon, ChartIcon, CheckIcon } from './hospitalVisuals';
import SEO from './SEO';
import './browseVetBills.css';

const SERVICE_OPTIONS = [
    { key: '', label: 'All services' },
    { key: 'officeVisit', label: 'Office visit', contributionLabel: 'office visit', article: 'an' },
    { key: 'vaccinations', label: 'Vaccinations', contributionLabel: 'vaccination', article: 'a' },
    { key: 'bloodTests', label: 'Blood tests', contributionLabel: 'blood test', article: 'a' },
    { key: 'xrays', label: 'X-rays', contributionLabel: 'X-ray', article: 'an' },
    { key: 'urinalysis', label: 'Urinalysis', contributionLabel: 'urinalysis', article: 'a' },
    { key: 'spayNeuter', label: 'Spay / neuter', contributionLabel: 'spay or neuter', article: 'a' },
    { key: 'fleaTickHeartworm', label: 'Flea / tick / heartworm', contributionLabel: 'flea, tick, or heartworm prevention', article: 'a' }
];

const SORT_OPTIONS = [
    { key: 'nearest', label: 'Nearest' },
    { key: 'lowestCost', label: 'Lowest selected price' },
    { key: 'evidence', label: 'Most uploads' },
    { key: 'name', label: 'Vet hospital name' },
    { key: 'city', label: 'City / state' }
];
const RADIUS_OPTIONS = [
    { value: '10', label: '10 miles' },
    { value: '25', label: '25 miles' },
    { value: '50', label: '50 miles' },
    { value: '100', label: '100 miles' },
    { value: 'any', label: 'Any distance' }
];

const normalizeSearchValue = (value = '') => String(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getServiceLabel = (selectedService) => {
    return SERVICE_OPTIONS.find(option => option.key === selectedService)?.label || 'Selected service';
};

const MISSING_PRICE_LABEL = 'No shared price yet';

const getServiceContributionLabel = (selectedService) => {
    return SERVICE_OPTIONS.find(option => option.key === selectedService)?.contributionLabel || getServiceLabel(selectedService).toLowerCase();
};

const getServiceArticle = (selectedService) => {
    return SERVICE_OPTIONS.find(option => option.key === selectedService)?.article || 'a';
};

const formatLocation = (hospital) => {
    const structuredLocation = [hospital?.city, hospital?.state].filter(Boolean).join(', ');
    const value = String(structuredLocation || hospital?.cityRegion || '').trim();
    return /^city\s*\d+(\s*,\s*[a-z]{2})?$/i.test(value) ? 'Location not listed' : value || 'Location not listed';
};

const hasPlaceholderLocation = (hospital) => {
    const city = String(hospital?.city || '').trim();
    const cityRegion = String(hospital?.cityRegion || '').trim();
    return /^city\s*\d+$/i.test(city) || /^city\s*\d+(\s*,\s*[a-z]{2})?$/i.test(cityRegion);
};

const getHospitalCoordinates = (hospital) => {
    if (hasPlaceholderLocation(hospital)) {
        return null;
    }

    if (Number.isFinite(Number(hospital?.coordinates?.latitude)) && Number.isFinite(Number(hospital?.coordinates?.longitude))) {
        return hospital.coordinates;
    }

    return null;
};

const getHospitalLocationSearchText = (hospital) => [
    hospital.city,
    hospital.state,
    hospital.zipCode,
    hospital.cityRegion,
    formatLocation(hospital)
].map(normalizeSearchValue).join(' ');

const getHospitalSearchText = (hospital) => [
    hospital.vetHospital
].map(normalizeSearchValue).join(' ');

const formatRange = (service) => {
    if (!service) {
        return 'No price yet';
    }

    if (Array.isArray(service)) {
        return service.length ? `${service.length} medication${service.length === 1 ? '' : 's'}` : 'No price yet';
    }

    const low = Number(service.low);
    const high = Number(service.high);

    if (!Number.isFinite(low) || !Number.isFinite(high)) {
        return 'No price yet';
    }

    return low === high ? `$${low.toFixed(2)}` : `$${low.toFixed(2)} - $${high.toFixed(2)}`;
};

const getSelectedService = (hospital, selectedService) => {
    if (!selectedService) {
        return null;
    }

    return hospital.services?.[selectedService] || null;
};

const hasComparablePrice = (hospital, selectedService) => {
    const service = getSelectedService(hospital, selectedService);
    return Boolean(service && !Array.isArray(service) && Number.isFinite(Number(service.low)) && Number.isFinite(Number(service.high)));
};

const getServiceConfidence = (hospital, selectedService) => {
    const service = getSelectedService(hospital, selectedService);

    if (!service || Array.isArray(service)) {
        return {
            label: 'Needs more prices',
            type: 'needs-data'
        };
    }

    const billCount = Number(service.billCount || 0);
    const estimateCount = Number(service.estimateCount || 0);

    if (billCount > 0 && estimateCount > 0) {
        return {
            label: 'Bills + estimates',
            type: 'mixed'
        };
    }

    if (billCount > 0) {
        return {
            label: 'From a paid bill',
            type: 'bill'
        };
    }

    if (estimateCount > 0) {
        return {
            label: 'From an estimate',
            type: 'estimate'
        };
    }

    return {
        label: 'Needs more prices',
        type: 'needs-data'
    };
};

const getServiceTrustText = (confidence) => {
    if (!confidence) {
        return '';
    }

    if (confidence.type === 'bill') {
        return 'Based on at least one real paid bill.';
    }

    if (confidence.type === 'estimate') {
        return 'Based on estimates — the final cost you pay may differ.';
    }

    if (confidence.type === 'mixed') {
        return 'Based on both paid bills and estimates.';
    }

    return 'No price shared yet. Another bill or estimate here would help.';
};

const getLowestCost = (hospital, selectedService) => {
    const service = getSelectedService(hospital, selectedService);
    if (!service || Array.isArray(service)) {
        return Number.POSITIVE_INFINITY;
    }

    const low = Number(service.low);
    return Number.isFinite(low) ? low : Number.POSITIVE_INFINITY;
};

const getUploadCount = (hospital) => (hospital.numberOfBills || 0) + (hospital.numberOfEstimates || 0);

const getSharedPriceExamples = (hospital) => SERVICE_OPTIONS
    .filter(option => option.key && hasComparablePrice(hospital, option.key))
    .slice(0, 4);

const getSharedPriceServiceCount = (hospital) => SERVICE_OPTIONS
    .filter(option => option.key && hasComparablePrice(hospital, option.key))
    .length;

const getPricingDataStrength = ({ uploadCount }) => {
    if (uploadCount >= 11) {
        return {
            label: 'High',
            type: 'strong'
        };
    }

    if (uploadCount >= 4) {
        return {
            label: 'Medium',
            type: 'growing'
        };
    }

    return {
        label: 'Low',
        type: 'early'
    };
};

const formatUploadedDocumentsLabel = (count) => (
    count === 1 ? '1 uploaded bill or estimate' : `${count} uploaded bills & estimates`
);

const getComparisonScope = ({ canUseDistance, locationTerm, locationZip, radiusMiles, resultCount }) => {
    const trimmedLocation = locationTerm.trim();

    if (canUseDistance && radiusMiles !== 'any') {
        return {
            title: `${resultCount} nearby vet hospital${resultCount === 1 ? '' : 's'}`,
            detail: `Showing vet hospitals within ${radiusMiles} miles of ${locationZip}.`
        };
    }

    if (canUseDistance && radiusMiles === 'any') {
        return {
            title: `${resultCount} shown vet hospital${resultCount === 1 ? '' : 's'}`,
            detail: `Showing all available vet hospitals. Zip ${locationZip} can still be used for nearest sorting.`
        };
    }

    if (trimmedLocation) {
        return {
            title: `${resultCount} matching vet hospital${resultCount === 1 ? '' : 's'}`,
            detail: `Showing vet hospital locations that match "${trimmedLocation}". Use a Zip code for nearby results.`
        };
    }

    return {
        title: `${resultCount} shown vet hospital${resultCount === 1 ? '' : 's'}`,
        detail: 'Showing all available VetBill uploads. Enter a City, State, or Zip to make prices local.'
    };
};

const getContributionHospitalScope = ({ canUseDistance, locationTerm, radiusMiles, count }) => {
    if (canUseDistance && radiusMiles !== 'any') {
        return `${count} nearby vet hospital${count === 1 ? '' : 's'}`;
    }

    if (locationTerm.trim()) {
        return `${count} matching vet hospital${count === 1 ? '' : 's'}`;
    }

    return `${count} vet hospital${count === 1 ? '' : 's'} in this comparison`;
};

const BrowseVetBills = () => {
    const [vetHospitals, setVetHospitals] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [locationTerm, setLocationTerm] = useState('');
    const [radiusMiles, setRadiusMiles] = useState('25');
    const [selectedService, setSelectedService] = useState('');
    const [selectedHospitals, setSelectedHospitals] = useState([]);
    const [showComparison, setShowComparison] = useState(false);
    const [sortKey, setSortKey] = useState('evidence');
    const [loading, setLoading] = useState(true);
    const [originCoordinates, setOriginCoordinates] = useState(null);
    const [autoLocation, setAutoLocation] = useState(null);
    const autoDetectRan = useRef(false);
    const navigate = useNavigate();
    const location = useLocation();
    const locationZip = normalizeZip(locationTerm);
    const canUseDistance = Boolean(originCoordinates);

    useEffect(() => {
        if (!locationZip) {
            setOriginCoordinates(null);
            return;
        }

        let cancelled = false;
        fetchCoordinatesForLocation(locationZip).then(coords => {
            if (!cancelled) {
                setOriginCoordinates(coords);
            }
        });

        return () => { cancelled = true; };
    }, [locationZip]);

    useEffect(() => {
        fetch('/api/vet-hospitals-summary')
            .then(response => response.json())
            .then(data => {
                setVetHospitals(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching vet hospitals:', error);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!selectedService && sortKey === 'lowestCost') {
            setSortKey('evidence');
        }

        if (!canUseDistance && sortKey === 'nearest') {
            setSortKey('evidence');
        }
    }, [selectedService, sortKey, canUseDistance]);

    useEffect(() => {
        setSearchTerm('');
        setLocationTerm('');
        setRadiusMiles('25');
        setSelectedService('');
        setSelectedHospitals([]);
        setShowComparison(false);
        setSortKey('evidence');
        autoDetectRan.current = false;
        setAutoLocation(null);
    }, [location.key]);

    // Default the location filter to the visitor's approximate area (from IP)
    // so a first-time visitor sees vets near them instead of a nationwide list.
    // Best-effort: on any failure the filter stays empty and all hospitals show.
    // Never overrides a location the user has already typed.
    useEffect(() => {
        if (autoDetectRan.current) return;
        autoDetectRan.current = true;

        let cancelled = false;
        fetchApproximateLocation().then(loc => {
            if (cancelled || !loc) return;
            const label = loc.zip || [loc.city, loc.state].filter(Boolean).join(', ');
            if (!label) return;
            // Do NOT auto-apply as a filter — IP geolocation (especially on
            // cellular) can resolve to a far-off city, which would strand the
            // visitor on "no vets near [wrong place]". Offer it as a one-tap
            // suggestion instead; the default view stays nationwide.
            setAutoLocation({ city: loc.city, state: loc.state, zip: loc.zip, label });
        });

        return () => { cancelled = true; };
    }, [location.key]);

    const filteredVetHospitals = useMemo(() => {
        const normalizedSearch = normalizeSearchValue(searchTerm);
        const normalizedLocation = normalizeSearchValue(locationTerm);
        const radiusLimit = radiusMiles === 'any' ? null : Number(radiusMiles);

        return vetHospitals
            .map(hospital => {
                const hospitalCoordinates = getHospitalCoordinates(hospital);
                const distanceMiles = canUseDistance ? getDistanceMiles(originCoordinates, hospitalCoordinates) : null;

                return { ...hospital, distanceMiles };
            })
            .filter(hospital => {
                const searchableText = getHospitalSearchText(hospital);
                const locationText = getHospitalLocationSearchText(hospital);
                const matchesLocationText = !normalizedLocation || locationText.includes(normalizedLocation);
                const matchesRadius = !canUseDistance
                    || radiusLimit === null
                    || (Number.isFinite(hospital.distanceMiles) && hospital.distanceMiles <= radiusLimit);

                return (
                    (!normalizedSearch || searchableText.includes(normalizedSearch)) &&
                    (canUseDistance ? matchesRadius : matchesLocationText)
                );
            })
            .sort((a, b) => {
                if (sortKey === 'nearest') {
                    const aDistance = Number.isFinite(a.distanceMiles) ? a.distanceMiles : Number.POSITIVE_INFINITY;
                    const bDistance = Number.isFinite(b.distanceMiles) ? b.distanceMiles : Number.POSITIVE_INFINITY;
                    return aDistance - bDistance;
                }

                if (sortKey === 'lowestCost') {
                    return getLowestCost(a, selectedService) - getLowestCost(b, selectedService);
                }

                if (sortKey === 'name') {
                    return (a.vetHospital || '').localeCompare(b.vetHospital || '');
                }

                if (sortKey === 'city') {
                    return (a.cityRegion || '').localeCompare(b.cityRegion || '');
                }

                const aEvidence = (a.numberOfBills || 0) + (a.numberOfEstimates || 0);
                const bEvidence = (b.numberOfBills || 0) + (b.numberOfEstimates || 0);
                return bEvidence - aEvidence;
            });
    }, [vetHospitals, searchTerm, locationTerm, radiusMiles, selectedService, sortKey, canUseDistance, originCoordinates]);

    const handleCardClick = (hospitalName) => {
        navigate(`/hospital/${encodeURIComponent(hospitalName)}`);
    };

    const handleServiceChange = (serviceKey) => {
        setSelectedService(serviceKey);
        setSortKey(serviceKey ? 'lowestCost' : 'evidence');
    };

    const handleSuggestedServiceClick = (serviceKey) => {
        setSearchTerm('');
        handleServiceChange(serviceKey);
    };

    const toggleHospitalComparison = (hospitalName) => {
        setSelectedHospitals(current => {
            if (current.includes(hospitalName)) {
                return current.filter(name => name !== hospitalName);
            }

            return current.length >= 4 ? current : [...current, hospitalName];
        });
    };

    const comparedHospitals = selectedHospitals
        .map(name => vetHospitals.find(hospital => hospital.vetHospital === name))
        .filter(Boolean);
    const comparisonServices = selectedService
        ? SERVICE_OPTIONS.filter(option => option.key === selectedService)
        : SERVICE_OPTIONS.filter(option => option.key);

    const hospitalsWithSelectedPrice = selectedService
        ? filteredVetHospitals.filter(hospital => hasComparablePrice(hospital, selectedService)).length
        : 0;
    const selectedServiceLabel = getServiceLabel(selectedService);
    const selectedServiceContributionLabel = getServiceContributionLabel(selectedService);
    const selectedServiceArticle = getServiceArticle(selectedService);
    const hospitalsNeedingSelectedPrice = selectedService
        ? Math.max(filteredVetHospitals.length - hospitalsWithSelectedPrice, 0)
        : 0;
    const serviceCoveragePercent = selectedService && filteredVetHospitals.length
        ? Math.round((hospitalsWithSelectedPrice / filteredVetHospitals.length) * 100)
        : 0;

    // Price spread for the selected service across the hospitals shown — powers
    // the "price range" hero band and the lowest-price highlight on each card.
    let priceRangeLow = null;
    let priceRangeHigh = null;
    if (selectedService) {
        filteredVetHospitals.forEach(hospital => {
            const service = getSelectedService(hospital, selectedService);
            if (service && !Array.isArray(service)) {
                const low = Number(service.low);
                const high = Number(service.high);
                if (Number.isFinite(low)) {
                    priceRangeLow = priceRangeLow === null ? low : Math.min(priceRangeLow, low);
                }
                if (Number.isFinite(high)) {
                    priceRangeHigh = priceRangeHigh === null ? high : Math.max(priceRangeHigh, high);
                }
            }
        });
    }
    const hasPriceRange = priceRangeLow !== null && priceRangeHigh !== null;
    const priceRangeSavings = hasPriceRange ? Math.round(priceRangeHigh - priceRangeLow) : 0;
    const comparisonScope = getComparisonScope({
        canUseDistance,
        locationTerm,
        locationZip,
        radiusMiles,
        resultCount: filteredVetHospitals.length
    });
    const contributionHospitalScope = getContributionHospitalScope({
        canUseDistance,
        locationTerm,
        radiusMiles,
        count: hospitalsNeedingSelectedPrice
    });

    const clearActiveService = () => {
        handleServiceChange('');
    };

    const handleControlsSubmit = (event) => {
        event.preventDefault();

        if (canUseDistance && !selectedService) {
            setSortKey('nearest');
        }

        event.currentTarget.querySelector('input[name="locationTerm"]')?.blur();
    };

    return (
        <div className="browse-page">
            <SEO title="Browse Vet Prices" description="Compare veterinary costs by hospital, service, and location. See real pricing data from the VetBill community." />
            <div className="browse-toolbar">
                <div className="browse-summary">
                    <h2>Compare Vet Prices</h2>
                </div>

                <div className="browse-controls-panel">
                    <div className="browse-controls-heading">
                        <div>
                            <h3>Find prices near you</h3>
                            <p>Pick a service, then add a City, State, or Zip if you want local results.</p>
                        </div>
                        <Link to="/upload/step1" className="browse-upload-link">Upload a bill or estimate</Link>
                    </div>

                    <form className="browse-controls" onSubmit={handleControlsSubmit}>
                        <label>
                            Vet hospital name
                            <input
                                type="search"
                                placeholder="Search by vet hospital name"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </label>

                        <label>
                            City, State, or Zip
                            <input
                                name="locationTerm"
                                type="search"
                                placeholder="Try 92637 or Irvine, CA"
                                value={locationTerm}
                                onChange={e => setLocationTerm(e.target.value)}
                            />
                        </label>
                        <label>
                            Within
                            <select value={radiusMiles} onChange={e => setRadiusMiles(e.target.value)} disabled={!canUseDistance}>
                                {RADIUS_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </label>

                        <label>
                            Service
                            <select value={selectedService} onChange={e => handleServiceChange(e.target.value)}>
                                {SERVICE_OPTIONS.map(option => (
                                    <option key={option.key} value={option.key}>{option.label}</option>
                                ))}
                            </select>
                        </label>

                        <label>
                            Sort
                            <select value={sortKey} onChange={e => setSortKey(e.target.value)}>
                                {SORT_OPTIONS.map(option => (
                                    <option
                                        key={option.key}
                                        value={option.key}
                                        disabled={(option.key === 'lowestCost' && !selectedService) || (option.key === 'nearest' && !canUseDistance)}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </form>

                    {!canUseDistance && locationTerm.trim() && (
                        <p className="location-helper-text">
                            For nearby results, enter a Zip code. City and State still narrow the hospital list.
                        </p>
                    )}

                    <div className="browse-quick-services">
                        <span>Quick pick:</span>
                        {SERVICE_OPTIONS.filter(option => option.key).map(option => (
                            <button
                                key={option.key}
                                type="button"
                                className={selectedService === option.key ? 'active' : ''}
                                onClick={() => handleSuggestedServiceClick(option.key)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    {!loading && (
                        <div className="browse-panel-footer">
                            Showing {filteredVetHospitals.length} {filteredVetHospitals.length === 1 ? 'hospital' : 'hospitals'}
                            {canUseDistance && radiusMiles !== 'any' ? ` within ${radiusMiles} miles of ${locationZip}` : ' · all locations'}
                            {!selectedService && ' · prices are examples from shared bills only'}
                            {selectedService && ` · ${hospitalsWithSelectedPrice} with ${selectedServiceLabel.toLowerCase()} pricing`}
                        </div>
                    )}
                </div>

                {selectedService && (
                    <div className="service-insight-group">
                        {hasPriceRange && (
                            <div className="price-range-hero">
                                <div className="price-range-hero-main">
                                    <span className="price-range-label">
                                        {selectedServiceLabel} price range{locationTerm.trim() ? ` in ${locationTerm.trim()}` : ''}
                                    </span>
                                    <div className="price-range-values">{formatDollars(priceRangeLow)} – {formatDollars(priceRangeHigh)}</div>
                                    <span className="price-range-sub">
                                        From {hospitalsWithSelectedPrice} vet hospital{hospitalsWithSelectedPrice === 1 ? '' : 's'} with shared prices
                                    </span>
                                </div>
                                <div className="price-range-bar">
                                    <div className="price-range-track" aria-hidden="true" />
                                    <div className="price-range-ends">
                                        <span><strong>{formatDollars(priceRangeLow)}</strong><small>lowest</small></span>
                                        <span><strong>{formatDollars(priceRangeHigh)}</strong><small>highest</small></span>
                                    </div>
                                </div>
                                {priceRangeSavings > 0 && (
                                    <div className="price-range-savings">
                                        Potential savings up to <strong>{formatDollars(priceRangeSavings)}</strong> by choosing a lower-priced option
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="service-insight-heading">
                            <div>
                                <h3>{selectedServiceLabel} comparison</h3>
                                <p>These numbers only describe the vet hospitals shown below.</p>
                            </div>
                            <button type="button" onClick={clearActiveService}>Clear service</button>
                        </div>

                        <div className="comparison-summary service-insight-strip">
                            <div>
                                <span>Area shown</span>
                                <strong>{comparisonScope.title}</strong>
                                <small>{comparisonScope.detail}</small>
                            </div>
                            <div>
                                <span>Vet hospitals with prices</span>
                                <strong>{hospitalsWithSelectedPrice}</strong>
                                <small>Vet hospitals below that already have a usable {selectedServiceLabel.toLowerCase()} price.</small>
                            </div>
                            <div>
                                <span>Needs more data</span>
                                <strong>{hospitalsNeedingSelectedPrice}</strong>
                                <small>Vet hospitals below where another bill or estimate would make this comparison better.</small>
                            </div>
                            <div>
                                <span>How complete</span>
                                <strong>{serviceCoveragePercent}%</strong>
                                <small>How much of the list currently has prices for this service.</small>
                            </div>
                        </div>
                    </div>
                )}

                {selectedService && hospitalsNeedingSelectedPrice > 0 && (
                    <div className="browse-contribution-callout">
                        <div>
                            <strong>Help improve {selectedServiceContributionLabel} prices</strong>
                            <span>
                                {contributionHospitalScope} {hospitalsNeedingSelectedPrice === 1 ? 'still needs' : 'still need'} {selectedServiceArticle} {selectedServiceContributionLabel} bill or estimate.
                            </span>
                        </div>
                        <Link to="/upload/step1">Share one</Link>
                    </div>
                )}

            </div>

            {showComparison && comparedHospitals.length > 0 && (
                <div className="comparison-overlay" role="dialog" aria-modal="true" aria-labelledby="comparison-title">
                    <section className="comparison-panel">
                        <div className="comparison-panel-header">
                            <div>
                                <h3 id="comparison-title">Side-by-side comparison</h3>
                                <p>Use this as a quick shortlist before opening each vet hospital’s full detail page.</p>
                            </div>
                            <button type="button" className="comparison-close-button" onClick={() => setShowComparison(false)}>
                                Close
                            </button>
                        </div>

                        <div className="comparison-table-wrap">
                            <table className="comparison-table">
                                <thead>
                                    <tr>
                                        <th>Vet hospital</th>
                                        {comparedHospitals.map(hospital => (
                                            <th key={hospital.vetHospital}>
                                                <button type="button" onClick={() => handleCardClick(hospital.vetHospital)}>
                                                    {hospital.vetHospital}
                                                </button>
                                                <span>{formatLocation(hospital)}</span>
                                                {Number.isFinite(hospital.distanceMiles) && <small>{hospital.distanceMiles.toFixed(1)} mi away</small>}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparisonServices.map(option => (
                                        <tr key={option.key}>
                                            <td>{option.label}</td>
                                            {comparedHospitals.map(hospital => {
                                                const service = getSelectedService(hospital, option.key);
                                                const hasPrice = hasComparablePrice(hospital, option.key);
                                                const confidence = getServiceConfidence(hospital, option.key);

                                                return (
                                                    <td key={`${hospital.vetHospital}-${option.key}`}>
                                                        <strong>{hasPrice ? formatRange(service) : MISSING_PRICE_LABEL}</strong>
                                                        {confidence && <span className={`confidence-badge ${confidence.type}`}>{confidence.label}</span>}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    <tr>
                                        <td>Based on</td>
                                        {comparedHospitals.map(hospital => (
                                            <td key={`${hospital.vetHospital}-evidence`}>
                                                <strong>{getUploadCount(hospital)} uploads</strong>
                                                <span>{hospital.numberOfBills || 0} bills / {hospital.numberOfEstimates || 0} estimates</span>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            )}

            {selectedHospitals.length > 0 && (
                <div className="shortlist-bar">
                    <div>
                        <strong>{selectedHospitals.length} selected</strong>
                        <span>{selectedHospitals.join(', ')}</span>
                    </div>
                    <div className="shortlist-actions">
                        <button type="button" onClick={() => setShowComparison(true)}>
                            Compare side by side
                        </button>
                        <button
                            type="button"
                            className="secondary-shortlist-action"
                            onClick={() => {
                                setSelectedHospitals([]);
                                setShowComparison(false);
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            )}

            {autoLocation && !locationTerm.trim() && (
                <div className="browse-auto-location">
                    <span>📍 Near {autoLocation.city ? `${autoLocation.city}, ${autoLocation.state}` : autoLocation.label}? Filter to vets in your area.</span>
                    <button
                        type="button"
                        onClick={() => setLocationTerm(autoLocation.label)}
                    >
                        Show vets near me
                    </button>
                </div>
            )}

            {autoLocation && locationTerm === autoLocation.label && (
                <div className="browse-auto-location">
                    <span>📍 Showing vets near {autoLocation.city ? `${autoLocation.city}, ${autoLocation.state}` : autoLocation.label}.</span>
                    <button
                        type="button"
                        onClick={() => { setLocationTerm(''); setAutoLocation(null); }}
                    >
                        Show all areas
                    </button>
                </div>
            )}

            {loading ? (
                <p className="browse-empty">Loading hospitals...</p>
            ) : filteredVetHospitals.length === 0 ? (
                locationTerm.trim() ? (
                    // A location is active but no hospitals match — invite the
                    // visitor to seed their own area rather than dead-ending,
                    // and (on a radius filter) offer to widen the distance since
                    // phone geolocation can be off.
                    <div className="browse-empty browse-empty-area">
                        <strong>No vet prices near {locationTerm.trim()} yet.</strong>
                        <span>VetBill is new and pricing data grows as pet owners upload. On a phone your location can also be off — widen the distance, be the first to add prices in your area, or show all areas.</span>
                        <div className="browse-empty-actions">
                            <Link to="/upload/step1" className="browse-empty-upload">Upload a bill or estimate</Link>
                            {canUseDistance && radiusMiles !== 'any' && (
                                <button type="button" onClick={() => setRadiusMiles('any')}>
                                    Show any distance
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchTerm('');
                                    setLocationTerm('');
                                    setAutoLocation(null);
                                    handleServiceChange('');
                                }}
                            >
                                Show all areas
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="browse-empty">
                        <strong>No matching vet hospitals yet.</strong>
                        <span>Try a common service name like X-rays, blood tests, office visit, or clear the filters.</span>
                        <button
                            type="button"
                            onClick={() => {
                                setSearchTerm('');
                                setLocationTerm('');
                                handleServiceChange('');
                            }}
                        >
                            Clear filters
                        </button>
                    </div>
                )
            ) : (
                <>
                    <div className="vet-hospital-cards">
                        {filteredVetHospitals.map((hospital) => {
                        const selectedServiceData = getSelectedService(hospital, selectedService);
                        const serviceRows = getSharedPriceExamples(hospital);
                        const uploadCount = getUploadCount(hospital);
                        const sharedPriceServiceCount = getSharedPriceServiceCount(hospital);
                        const pricingDataStrength = getPricingDataStrength({ uploadCount });
                        const avatar = getHospitalAvatar(hospital.vetHospital);
                        const selectedHasPrice = selectedService ? hasComparablePrice(hospital, selectedService) : false;
                        const serviceConfidence = selectedService ? getServiceConfidence(hospital, selectedService) : null;
                        const isSelectedForComparison = selectedHospitals.includes(hospital.vetHospital);
                        const selectionDisabled = !isSelectedForComparison && selectedHospitals.length >= 4;
                        const hospitalLow = selectedHasPrice ? Number(getSelectedService(hospital, selectedService)?.low) : null;
                        const isLowestPrice = selectedHasPrice && hasPriceRange && hospitalsWithSelectedPrice > 1
                            && Number.isFinite(hospitalLow) && hospitalLow === priceRangeLow;
                        const hasRealBills = (hospital.numberOfBills || 0) > 0;
                        const estimatesOnly = !hasRealBills && (hospital.numberOfEstimates || 0) > 0;

                        return (
                            <article
                                key={hospital.vetHospital}
                                className={`vet-hospital-card ${selectedService ? 'compare-mode-card' : ''} ${selectedHasPrice ? 'has-price' : ''} ${isSelectedForComparison ? 'selected-for-comparison' : ''}`}
                                onClick={() => handleCardClick(hospital.vetHospital)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        handleCardClick(hospital.vetHospital);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                            >
                                <div className="hospital-card-top">
                                    <span className="hospital-avatar" style={{ background: avatar.color }} aria-hidden="true">{avatar.initials}</span>
                                    <div className="hospital-card-heading-text">
                                        <h3>{hospital.vetHospital}</h3>
                                        <p className="hospital-location">
                                            <PinIcon />
                                            {formatLocation(hospital)}
                                            {Number.isFinite(hospital.distanceMiles) && (
                                                <span>{hospital.distanceMiles.toFixed(1)} mi away</span>
                                            )}
                                        </p>
                                    </div>
                                </div>

                                <div className="hospital-card-upload-count">
                                    <DocIcon />
                                    {formatUploadedDocumentsLabel(uploadCount)}
                                </div>

                                {(hasRealBills || estimatesOnly || isLowestPrice) && (
                                    <div className="hospital-card-badges">
                                        {isLowestPrice && (
                                            <span className="hcard-badge badge-lowest">Lowest price</span>
                                        )}
                                        {hasRealBills ? (
                                            <span className="hcard-badge badge-bills"><CheckIcon /> From real bills</span>
                                        ) : estimatesOnly ? (
                                            <span className="hcard-badge badge-estimates">Estimates only</span>
                                        ) : null}
                                    </div>
                                )}

                                {selectedService ? (
                                    <div className={`selected-service ${selectedHasPrice ? '' : 'missing-service-price'}`}>
                                        <div>
                                            <span>{getServiceLabel(selectedService)}</span>
                                            <strong className={`selected-service-price ${isLowestPrice ? 'is-lowest' : ''}`}>{selectedHasPrice ? formatRange(selectedServiceData) : MISSING_PRICE_LABEL}</strong>
                                        </div>
                                        {serviceConfidence && (
                                            <>
                                                <small className={`confidence-badge ${serviceConfidence.type}`}>
                                                    {serviceConfidence.label}
                                                </small>
                                                <small className="service-confidence-help">
                                                    {getServiceTrustText(serviceConfidence)}
                                                </small>
                                            </>
                                        )}
                                        {!selectedHasPrice && (
                                            <Link
                                                to="/upload/step1"
                                                className="card-upload-prompt"
                                                onClick={(event) => event.stopPropagation()}
                                                onKeyDown={(event) => event.stopPropagation()}
                                            >
                                                Share {selectedServiceArticle} {selectedServiceContributionLabel} bill
                                            </Link>
                                        )}
                                    </div>
                                ) : (
                                    <div className="service-grid">
                                        <div className="service-grid-header">
                                            <span>Pricing Examples</span>
                                        </div>
                                        {serviceRows.length ? serviceRows.map(option => {
                                            const service = hospital.services?.[option.key];

                                            return (
                                            <div key={option.key} className="service-row">
                                                <span>{option.label}</span>
                                                <strong>{formatRange(service)}</strong>
                                            </div>
                                            );
                                        }) : (
                                            <p className="service-grid-empty">
                                                No pricing examples yet.
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className="pricing-data-summary">
                                    <div>
                                        <span className="pricing-data-label"><ChartIcon /> How much data</span>
                                        <strong className={`pricing-data-badge ${pricingDataStrength.type}`}>
                                            {pricingDataStrength.label}
                                        </strong>
                                    </div>
                                    <p>
                                        {sharedPriceServiceCount} service{sharedPriceServiceCount === 1 ? '' : 's'} with prices
                                    </p>
                                </div>

                                {selectedService && (
                                    <div className="hospital-card-footer">
                                        <button
                                            type="button"
                                            className={`compare-select-button ${isSelectedForComparison ? 'active' : ''}`}
                                            disabled={selectionDisabled}
                                            onKeyDown={(event) => event.stopPropagation()}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                toggleHospitalComparison(hospital.vetHospital);
                                            }}
                                        >
                                            {isSelectedForComparison ? 'Selected' : 'Compare'}
                                        </button>
                                    </div>
                                )}
                            </article>
                        );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default BrowseVetBills;
